import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { Text } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';

import PropertyConstants from '../../../../constants/PropertyConstants';
import ConnectedNetworkView from '../../../../components/ConnectedNetworkView';

import type { SyndicationProps } from '../../../../model/Syndication';

const { SYNDICATION_OVERVIEW_STATUS } = PropertyConstants;

type Props = {
  syndication: SyndicationProps;
};

const LearnMore = ({ syndication }: Props) => {
  const { isMultifamily } = useSelector(
    (state) => ({
      isMultifamily: state?.properties?.currentProperty?.isMultifamily,
    }),
    shallowEqual,
  );

  const {
    overview: { status: syndicationOverviewStatus },

    // external
    realtor: { url: realtorUrl } = {},
    rent: { url: rentUrl } = {},
    redfin: { url: redfinUrl } = {},
    apartmentGuide: { url: apartmentGuideUrl } = {},
  } = syndication;

  /**
   * TODO: create a utility function to return this in multiple places
   * getPublishingStatus({syndication, isRoomForRent})
   * return string and let the container set the text formatting and spacing
   */
  const possiblePublishSites = useMemo(() => {
    /**
     * ["zillow", "trulia", "hotpads", "realtor", "rent", "apartmentGuide", "redfin"] if MF,
     * else ["zillow", "trulia", "hotpads"]
     **/
    let possiblePublishSitesState = 3;

    if (isMultifamily) {
      possiblePublishSitesState =
        possiblePublishSitesState +
        (realtorUrl ? 1 : 0) +
        (rentUrl ? 1 : 0) +
        (apartmentGuideUrl ? 1 : 0) +
        (redfinUrl ? 1 : 0);
    }

    return possiblePublishSitesState;
  }, [isMultifamily, realtorUrl, rentUrl, apartmentGuideUrl, redfinUrl]);

  const renderPublishText = () => {
    if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHED) {
      return 'View on Zillow Rental Network';
    }
    if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHING) {
      return `Published to ${syndication.overview.publishedCount} of ${possiblePublishSites} sites...`;
    }

    if (syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.INACTIVE) {
      return (
        <Text
          css={{
            textStyle: { base: 'body-xs', lg: 'body-sm-bold', xl: 'body' },
          }}
        >
          Publish to the Zillow Rental Network
        </Text>
      );
    }

    return null;
  };

  return (
    <>
      {syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHED ||
      syndicationOverviewStatus === SYNDICATION_OVERVIEW_STATUS.PUBLISHING ? (
        <ConnectedNetworkView syndication={syndication} label={renderPublishText()} />
      ) : (
        <div data-testid="LearnMore-Text">{renderPublishText()}</div>
      )}
    </>
  );
};

export default LearnMore;
